// File: Index.tsx (replace existing file)
// Cleaner UI: smaller fonts, whole card clickable (no Go button), keyboard accessible,
// nicer spacing and subtle hover, gradient backgrounds retained.

import { useState } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import Icon from '../icon'
import moment from 'moment'
import apiUrl from '../../configs/constant';
import { useNavigate } from 'react-router-dom'

interface Props {
  mmgudangId?: number | string | null
}

export default function Index({ mmgudangId }: Props) {
  const navigate = useNavigate();
  const [openExportDialog, setOpenExportDialog] = useState(false)
  const [exportType, setExportType] = useState<null | 3 | 4>(null)
  const [expTglAwal, setExpTglAwal] = useState<string>(moment().subtract(3, 'months').startOf('month').format('YYYY-MM-DD'))
  const [expTglAkhir, setExpTglAkhir] = useState<string>(moment().endOf('month').format('YYYY-MM-DD'))

  const openUrl = (url: string) => {
    window.open(url, '_blank', 'noopener noreferrer')
  }

  const handleOpenDirectImport = () => {
    navigate('/system/pos/import')
  }

  const handleOpenDirectExport = () => {
    openUrl('https://apinya.com/export-stock')
  }

  const handleOpenRangeExport = (type: 3 | 4) => {
    setExportType(type)
    setOpenExportDialog(true)
  }

  const handleConfirmRangeExport = () => {
    if (!exportType) return
    const awal = expTglAwal
    const akhir = expTglAkhir
    let url = ''
    if (exportType === 3) {
      url = `${apiUrl.baseUrl}/warehouse/gudang/export-mutasi-in-gudang-17?tgl_awal=${encodeURIComponent(awal)}&tgl_akhir=${encodeURIComponent(akhir)}`
    } else {
      url = `${apiUrl.baseUrl}/warehouse/gudang/export-mutasi-out-gudang-17?tgl_awal=${encodeURIComponent(awal)}&tgl_akhir=${encodeURIComponent(akhir)}`
    }
    openUrl(url)
    setOpenExportDialog(false)
    setExportType(null)
  }

  // Reusable CardAction: whole card clickable, keyboard accessible
  const CardAction: React.FC<{
    title: string
    subtitle?: string
    icon: string
    onClick: () => void
    bg?: string
  }> = ({ title, subtitle, icon, onClick, bg }) => {
    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    }

    return (
      <Box
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
        aria-label={title}
        sx={{
          borderRadius: 2,
          p: { xs: 2, md: 2.5 },
          boxShadow: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          transition: 'transform .14s ease, box-shadow .14s ease',
          background: bg || 'transparent',
          color: 'common.white',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: 6,
          },
          '&:focus': {
            outline: (theme) => `2px solid ${theme.palette.action.selected}`,
            transform: 'translateY(-6px)',
            boxShadow: 6,
          },
          minHeight: 84,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.12)',
            flexShrink: 0,
          }}
        >
          <Icon icon={icon} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.1 }}
            noWrap
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.78rem', opacity: 0.9 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <CardAction
            title="Import Dari BigSeller"
            subtitle="Tarik data stok terbaru"
            icon="mdi:download"
            onClick={handleOpenDirectImport}
            bg="linear-gradient(120deg,#0984e3 0%, #74b9ff 100%)"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <CardAction
            title="Export Stock Ke BigSeller"
            subtitle="Kirim data stok saat ini"
            icon="mdi:upload"
            onClick={handleOpenDirectExport}
            bg="linear-gradient(120deg,#00b894 0%, #55efc4 100%)"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <CardAction
            title="Export Stok Masuk Ke BigSeller"
            subtitle="Pilih rentang tanggal untuk stok masuk"
            icon="mdi:tray-arrow-down"
            onClick={() => handleOpenRangeExport(3)}
            bg="linear-gradient(120deg,#6c5ce7 0%, #a29bfe 100%)"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <CardAction
            title="Export Stok Keluar Ke BigSeller"
            subtitle="Pilih rentang tanggal untuk stok keluar"
            icon="mdi:tray-arrow-up"
            onClick={() => handleOpenRangeExport(4)}
            bg="linear-gradient(120deg,#e17055 0%, #fab1a0 100%)"
          />
        </Grid>
      </Grid>

      {/* <Box sx={{ mt: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
          Klik seluruh card untuk menjalankan aksi — keyboard accessible (Enter / Space).
        </Typography>
      </Box> */}

      <Dialog open={openExportDialog} onClose={() => setOpenExportDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem', color: '#000' }}>
          {exportType === 3 ? 'Export Stok Masuk' : exportType === 4 ? 'Export Stok Keluar' : 'Export'}
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mt: 1 }}>
            <TextField
              label="Tanggal Awal"
              type="date"
              value={expTglAwal}
              onChange={(e) => setExpTglAwal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Tanggal Akhir"
              type="date"
              value={expTglAkhir}
              onChange={(e) => setExpTglAkhir(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setOpenExportDialog(false)
              setExportType(null)
            }}
            size="small"
          >
            Batal
          </Button>
          <Button variant="contained" onClick={handleConfirmRangeExport} size="small">
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
